import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BookMarked, ArrowLeft, ChevronRight, Plus, Send, User,
  Zap, EyeOff, Lock, Unlock, ArrowRight, FileText,
  CheckCircle2, XCircle, MinusCircle, TriangleAlert, Clock,
  AlertTriangle, Pencil, MessageSquare, Sparkles, Signal,
  Target, X, RefreshCw, History,
} from "lucide-react";

const STATUS_CONFIG = {
  hypothesis: { label: "Hypothesis", bg: "bg-indigo-500/15", text: "text-indigo-400", border: "border-indigo-500/30" },
  validated_in_conversation: { label: "Validated", bg: "bg-sky-500/15", text: "text-sky-400", border: "border-sky-500/30" },
  converted_to_proposal: { label: "Proposal", bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" },
  won: { label: "Won", bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30" },
  lost: { label: "Lost", bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
  expansion_opportunity: { label: "Expansion", bg: "bg-violet-500/15", text: "text-violet-400", border: "border-violet-500/30" },
  content_theme_extracted: { label: "Content Extracted", bg: "bg-teal-500/15", text: "text-teal-400", border: "border-teal-500/30" },
  archived: { label: "Archived", bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/30" },
};

const POSTURE_STYLES = {
  "Authority-led direct outreach": "text-emerald-400",
  "Insight-led conversation": "text-sky-400",
  "Referral-led introduction": "text-violet-400",
  "Nurture first, then engage": "text-amber-400",
  "No action yet": "text-slate-400",
};

const CONFIDENCE_STYLES = {
  "High": "text-emerald-400", "Medium": "text-amber-400",
  "Low": "text-orange-400", "Insufficient": "text-red-400",
};

const SIGNAL_STYLES = {
  "high": { text: "text-emerald-400", bg: "bg-emerald-900/20", border: "border-emerald-700/30" },
  "medium": { text: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
  "low": { text: "text-slate-400", bg: "bg-slate-800/40", border: "border-slate-700/30" },
};

const ENRICHMENT_STYLES = {
  "ready_for_analysis": { label: "Ready for Analysis", text: "text-emerald-400", bg: "bg-emerald-900/20", border: "border-emerald-700/30" },
  "partially_ready": { label: "Partially Ready", text: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
  "not_ready_for_analysis": { label: "Needs Enrichment", text: "text-slate-400", bg: "bg-slate-800/40", border: "border-slate-700/30" },
  "analysis_completed": { label: "Enriched", text: "text-violet-400", bg: "bg-violet-900/20", border: "border-violet-700/30" },
  "reanalysis_recommended": { label: "Reanalysis Recommended", text: "text-orange-400", bg: "bg-orange-900/20", border: "border-orange-700/30" },
};

const VALID_STATUSES = [
  "hypothesis", "validated_in_conversation", "converted_to_proposal",
  "won", "lost", "expansion_opportunity", "content_theme_extracted", "archived",
];

const OUTCOME_QUALITIES = [
  "strong-fit", "partial-fit", "poor-fit", "no-budget",
  "timing-issue", "relationship-blocked", "wrong-buyer", "lost-to-competitor",
];

const OPPORTUNITY_SOURCES = [
  { value: "referral", label: "Referral" },
  { value: "existing_client_expansion", label: "Existing Client Expansion" },
  { value: "dormant_reactivation", label: "Dormant Reactivation" },
  { value: "event_conference", label: "Event / Conference" },
  { value: "board_senior_network", label: "Board / Senior Network" },
  { value: "founder_instinct", label: "Founder Instinct" },
  { value: "inbound_enquiry", label: "Inbound Enquiry" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "partner_donor_ecosystem", label: "Partner / Donor Ecosystem" },
  { value: "media_speaking_exposure", label: "Media / Speaking Exposure" },
  { value: "other", label: "Other" },
];

const SECTORS = [
  "Financial Services", "Technology", "Energy & Resources", "Telecommunications",
  "Development Finance", "Professional Services", "Public Sector",
  "FMCG", "Manufacturing", "Healthcare",
];

function SectionLabel({ children }) {
  return <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">{children}</p>;
}

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE — List or Detail based on route
// ═══════════════════════════════════════════════════════════════════

export default function CommercialBriefs() {
  const { briefId } = useParams();
  return briefId ? <BriefDetail briefId={briefId} /> : <BriefList />;
}


// ═══════════════════════════════════════════════════════════════════
// BRIEF LIST
// ═══════════════════════════════════════════════════════════════════

function BriefList() {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showQuickCreate, setShowQuickCreate] = useState(false);

  const fetchBriefs = useCallback(async () => {
    try {
      const params = filter ? `?status=${filter}` : "";
      const res = await fetch(`${API}/commercial-briefs/${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setBriefs(data.briefs || []);
      }
    } catch {} finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchBriefs(); }, [fetchBriefs]);

  const handleQuickCreateSuccess = (newBrief) => {
    setShowQuickCreate(false);
    fetchBriefs();
    navigate(`/commercial-briefs/${newBrief.brief_id}`);
  };

  return (
    <div className="min-h-screen bg-[#0a0e17] text-slate-200" data-testid="briefs-list-page">
      <div className="border-b border-slate-800/60 bg-[#0d1220]">
        <div className="max-w-[1200px] mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <BookMarked className="w-5 h-5 text-emerald-500" />
                <h1 className="text-lg font-bold tracking-tight text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="briefs-title">
                  Commercial Briefs
                </h1>
                <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500 ml-1">{briefs.length}</Badge>
              </div>
              <p className="text-xs text-slate-500">Persistent commercial memory — hypotheses, reasoning, and outcome tracking.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:text-white hover:border-slate-600"
                onClick={() => setShowQuickCreate(true)} data-testid="quick-create-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Quick Create
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => navigate("/entry-intelligence")} data-testid="run-analysis-btn">
                <Target className="w-3.5 h-3.5 mr-1.5" /> Run Analysis
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex gap-1.5 mb-5 flex-wrap">
          <Button size="sm" variant={filter === "" ? "default" : "ghost"}
            className={filter === "" ? "bg-slate-800 text-slate-200" : "text-slate-500"} onClick={() => setFilter("")}>All</Button>
          {VALID_STATUSES.filter(s => s !== "archived").map(s => {
            const sc = STATUS_CONFIG[s];
            return (
              <Button key={s} size="sm" variant={filter === s ? "default" : "ghost"}
                className={filter === s ? `${sc.bg} ${sc.text}` : "text-slate-500"} onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}>
                {sc.label}
              </Button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500">Loading briefs...</div>
        ) : briefs.length === 0 ? (
          <div className="text-center py-20">
            <BookMarked className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{filter ? "No briefs with this status" : "No commercial briefs yet"}</p>
            <p className="text-xs text-slate-600 mt-1">Capture a hypothesis with Quick Create, or run an Entry Intelligence analysis.</p>
            <div className="flex justify-center gap-2 mt-4">
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
                onClick={() => setShowQuickCreate(true)}>
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Quick Create
              </Button>
              <Button size="sm" className="bg-emerald-600 text-white"
                onClick={() => navigate("/entry-intelligence")}>
                <Target className="w-3.5 h-3.5 mr-1.5" /> Run Analysis
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="briefs-list">
            {briefs.map(b => {
              const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.hypothesis;
              const isQuickCreate = b.source === "quick_create";
              const enrichStyle = ENRICHMENT_STYLES[b.enrichment_readiness] || ENRICHMENT_STYLES.not_ready_for_analysis;
              const sigStyle = SIGNAL_STYLES[b.signal_strength] || SIGNAL_STYLES.medium;
              
              return (
                <button key={b.brief_id} onClick={() => navigate(`/commercial-briefs/${b.brief_id}`)}
                  className="w-full text-left p-4 rounded-lg bg-[#111827] border border-slate-800/40 hover:border-slate-700/60 transition-all group"
                  data-testid={`brief-row-${b.brief_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-200 group-hover:text-white truncate">{b.account_name}</p>
                        <Badge className={`text-[9px] ${sc.bg} ${sc.text} ${sc.border} border`}>{sc.label}</Badge>
                        {b.hold_active && <Badge className="text-[9px] bg-amber-900/20 text-amber-400 border-amber-700/30 border">Hold</Badge>}
                        {isQuickCreate && (
                          <Badge className={`text-[9px] ${enrichStyle.bg} ${enrichStyle.text} ${enrichStyle.border} border`}>
                            {enrichStyle.label}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {b.selected_wedge ? (
                          <span className="text-xs text-slate-400">{b.selected_wedge}</span>
                        ) : b.initial_tension_hypothesis ? (
                          <span className="text-xs text-slate-500 italic truncate max-w-[300px]">{b.initial_tension_hypothesis}</span>
                        ) : null}
                        {b.commercial_posture && <span className={`text-[10px] ${POSTURE_STYLES[typeof b.commercial_posture === 'object' ? b.commercial_posture.posture : b.commercial_posture] || "text-slate-500"}`}>{typeof b.commercial_posture === 'object' ? b.commercial_posture.posture : b.commercial_posture}</span>}
                        {isQuickCreate && b.opportunity_source && (
                          <span className="text-[10px] text-slate-600">
                            via {OPPORTUNITY_SOURCES.find(s => s.value === b.opportunity_source)?.label || b.opportunity_source}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      {isQuickCreate && b.signal_strength && (
                        <div className="flex items-center gap-1">
                          <Signal className={`w-3 h-3 ${sigStyle.text}`} />
                          <span className={`text-[10px] ${sigStyle.text}`}>{b.signal_strength}</span>
                        </div>
                      )}
                      {b.confidence_level && (
                        <span className={`text-xs font-medium ${CONFIDENCE_STYLES[b.confidence_level] || "text-slate-500"}`}>{b.confidence_level}</span>
                      )}
                      <div className="text-right">
                        {b.commercial_owner && <p className="text-[10px] text-slate-500">{b.commercial_owner}</p>}
                        <p className="text-[10px] text-slate-600">{b.created_at?.slice(0, 10)}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-slate-500" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Create Modal */}
      {showQuickCreate && (
        <QuickCreateModal onClose={() => setShowQuickCreate(false)} onSuccess={handleQuickCreateSuccess} />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// QUICK CREATE MODAL — Fast capture commercial hypothesis
// ═══════════════════════════════════════════════════════════════════

function QuickCreateModal({ onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("manual"); // manual | email
  const [form, setForm] = useState({
    account_name: "",
    sector: "",
    known_buyer_contact: "",
    initial_tension_hypothesis: "",
    opportunity_source: "referral",
    signal_strength: "medium",
    confidence_level: "Low",
    next_conversation_intent: "",
  });
  
  // Email ingest state
  const [emailContent, setEmailContent] = useState("");
  const [emailSender, setEmailSender] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailProvenance, setEmailProvenance] = useState(null);
  const [parsingEmail, setParsingEmail] = useState(false);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canSave = form.account_name.trim().length > 0;

  const handleParseEmail = async () => {
    if (!emailContent.trim()) return;
    setParsingEmail(true);
    try {
      const res = await fetch(`${API}/commercial-briefs/email-ingest/preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_content: emailContent,
          sender: emailSender,
          subject: emailSubject,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.is_excluded) {
          toast.warning(data.exclusion_reason || "This email appears to be low-signal");
        }
        setEmailDraft(data.draft);
        setEmailProvenance(data.email_provenance);
        // Pre-fill form with draft
        setForm({
          ...form,
          ...data.draft,
        });
      } else {
        toast.error("Failed to parse email");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setParsingEmail(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Include email provenance if from email ingest
      if (mode === "email" && emailProvenance) {
        payload.email_provenance = emailProvenance;
      }
      
      const res = await fetch(`${API}/commercial-briefs/quick-create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const brief = await res.json();
        toast.success("Commercial hypothesis captured");
        onSuccess(brief);
      } else {
        toast.error("Failed to create brief");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="quick-create-modal">
      <div className="bg-[#111827] border border-slate-800/60 rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800/60">
          <div>
            <h2 className="text-base font-bold text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }}>
              Quick Create
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Capture a commercial hypothesis — enrich later</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-800 text-slate-500 hover:text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode Tabs */}
        <div className="flex border-b border-slate-800/60">
          <button
            className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${mode === "manual" ? "text-emerald-400 border-b-2 border-emerald-500 bg-emerald-500/5" : "text-slate-500 hover:text-slate-400"}`}
            onClick={() => setMode("manual")}
            data-testid="mode-manual"
          >
            Manual Capture
          </button>
          <button
            className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${mode === "email" ? "text-violet-400 border-b-2 border-violet-500 bg-violet-500/5" : "text-slate-500 hover:text-slate-400"}`}
            onClick={() => setMode("email")}
            data-testid="mode-email"
          >
            Import from Email
          </button>
        </div>

        {/* Email Import Mode */}
        {mode === "email" && !emailDraft && (
          <div className="p-4 space-y-4">
            <div className="rounded-lg border border-violet-700/30 bg-violet-950/20 p-3">
              <p className="text-xs text-violet-300 font-medium">Paste email content below</p>
              <p className="text-[10px] text-violet-400/70 mt-0.5">The system will classify the email, extract key details, and create a draft hypothesis for your review.</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <SectionLabel>Sender (optional)</SectionLabel>
                <input
                  className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-violet-600"
                  placeholder="e.g. john@company.com"
                  value={emailSender}
                  onChange={e => setEmailSender(e.target.value)}
                  data-testid="email-sender"
                />
              </div>
              <div>
                <SectionLabel>Subject (optional)</SectionLabel>
                <input
                  className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-violet-600"
                  placeholder="e.g. Re: Leadership support"
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  data-testid="email-subject"
                />
              </div>
            </div>

            <div>
              <SectionLabel>Email Content *</SectionLabel>
              <textarea
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-violet-600 resize-none"
                rows={8}
                placeholder="Paste the email content here..."
                value={emailContent}
                onChange={e => setEmailContent(e.target.value)}
                data-testid="email-content"
              />
            </div>

            <Button
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
              size="sm"
              onClick={handleParseEmail}
              disabled={!emailContent.trim() || parsingEmail}
              data-testid="parse-email-btn"
            >
              {parsingEmail ? "Analyzing..." : "Parse Email & Preview Draft"}
            </Button>
          </div>
        )}

        {/* Email Draft Preview */}
        {mode === "email" && emailDraft && (
          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="rounded-lg border border-emerald-700/30 bg-emerald-950/20 p-3">
              <p className="text-xs text-emerald-300 font-medium">Draft Hypothesis Preview</p>
              <p className="text-[10px] text-emerald-400/70 mt-0.5">Review and edit before saving. Classification: {emailProvenance?.classification}</p>
            </div>
            
            {/* Show extracted info */}
            <div>
              <SectionLabel>Account / Opportunity Name *</SectionLabel>
              <input
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-600"
                value={form.account_name}
                onChange={e => updateField("account_name", e.target.value)}
                data-testid="qc-account-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <SectionLabel>Source (detected)</SectionLabel>
                <select
                  className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                  value={form.opportunity_source}
                  onChange={e => updateField("opportunity_source", e.target.value)}
                >
                  {OPPORTUNITY_SOURCES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <SectionLabel>Signal Strength (detected)</SectionLabel>
                <select
                  className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                  value={form.signal_strength}
                  onChange={e => updateField("signal_strength", e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div>
              <SectionLabel>Known Buyer / Contact (extracted)</SectionLabel>
              <input
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                value={form.known_buyer_contact}
                onChange={e => updateField("known_buyer_contact", e.target.value)}
              />
            </div>

            <div>
              <SectionLabel>Initial Tension / Hypothesis (extracted)</SectionLabel>
              <textarea
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600 resize-none"
                rows={2}
                value={form.initial_tension_hypothesis}
                onChange={e => updateField("initial_tension_hypothesis", e.target.value)}
              />
            </div>

            <div>
              <SectionLabel>Sector</SectionLabel>
              <select
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                value={form.sector}
                onChange={e => updateField("sector", e.target.value)}
              >
                <option value="">— Select —</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {emailProvenance && (
              <div className="text-[10px] text-slate-500 p-2 rounded bg-slate-900/50">
                <p>Email preview: {emailProvenance.body_preview?.slice(0, 100)}...</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-slate-700 text-slate-400"
                onClick={() => { setEmailDraft(null); setEmailProvenance(null); }}
              >
                Back to Email
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                size="sm"
                onClick={handleSave}
                disabled={!canSave || saving}
                data-testid="qc-save-btn"
              >
                {saving ? "Saving..." : "Confirm & Save Brief"}
              </Button>
            </div>
          </div>
        )}

        {/* Manual Mode Form */}
        {mode === "manual" && (
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Account Name — Required */}
          <div>
            <SectionLabel>Account / Opportunity Name *</SectionLabel>
            <input
              className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-600"
              placeholder="e.g. Okafor referral, Lagos conference lead"
              value={form.account_name}
              onChange={e => updateField("account_name", e.target.value)}
              autoFocus
              data-testid="qc-account-name"
            />
          </div>

          {/* Source + Signal Strength row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <SectionLabel>Source of Opportunity</SectionLabel>
              <select
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                value={form.opportunity_source}
                onChange={e => updateField("opportunity_source", e.target.value)}
                data-testid="qc-source"
              >
                {OPPORTUNITY_SOURCES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <SectionLabel>Signal Strength</SectionLabel>
              <select
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                value={form.signal_strength}
                onChange={e => updateField("signal_strength", e.target.value)}
                data-testid="qc-signal"
              >
                <option value="low">Low — early instinct</option>
                <option value="medium">Medium — warm signal</option>
                <option value="high">High — concrete trigger</option>
              </select>
            </div>
          </div>

          {/* Sector + Confidence row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <SectionLabel>Sector</SectionLabel>
              <select
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                value={form.sector}
                onChange={e => updateField("sector", e.target.value)}
                data-testid="qc-sector"
              >
                <option value="">— Unknown —</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <SectionLabel>Confidence This Is Real</SectionLabel>
              <select
                className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                value={form.confidence_level}
                onChange={e => updateField("confidence_level", e.target.value)}
                data-testid="qc-confidence"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>

          {/* Known Buyer / Contact */}
          <div>
            <SectionLabel>Known Buyer / Contact</SectionLabel>
            <input
              className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
              placeholder="e.g. CEO referred by Tunde, Head of HR"
              value={form.known_buyer_contact}
              onChange={e => updateField("known_buyer_contact", e.target.value)}
              data-testid="qc-buyer"
            />
          </div>

          {/* Initial Tension / Hypothesis */}
          <div>
            <SectionLabel>Initial Tension or Commercial Hypothesis</SectionLabel>
            <textarea
              className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600 resize-none"
              rows={2}
              placeholder="What do you think the commercial angle might be?"
              value={form.initial_tension_hypothesis}
              onChange={e => updateField("initial_tension_hypothesis", e.target.value)}
              data-testid="qc-tension"
            />
          </div>

          {/* Next Conversation Intent */}
          <div>
            <SectionLabel>Next Conversation Intent</SectionLabel>
            <input
              className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
              placeholder="e.g. Follow up after Easter, Wait for board meeting"
              value={form.next_conversation_intent}
              onChange={e => updateField("next_conversation_intent", e.target.value)}
              data-testid="qc-next-intent"
            />
          </div>
        </div>
        )}

        {/* Footer — only show for manual mode */}
        {mode === "manual" && (
        <div className="flex items-center justify-between p-4 border-t border-slate-800/60 bg-slate-900/30">
          <p className="text-[10px] text-slate-600">Capture now, reason later</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-slate-500" onClick={onClose}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleSave} disabled={!canSave || saving} data-testid="qc-save-btn">
              {saving ? "Saving..." : "Capture Hypothesis"}
            </Button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// BRIEF DETAIL
// ═══════════════════════════════════════════════════════════════════

function BriefDetail({ briefId }) {
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBrief = useCallback(async () => {
    try {
      const res = await fetch(`${API}/commercial-briefs/${briefId}`, { credentials: "include" });
      if (res.ok) setBrief(await res.json());
      else toast.error("Brief not found");
    } catch {} finally { setLoading(false); }
  }, [briefId]);

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  if (loading) return <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center"><div className="text-slate-500">Loading...</div></div>;
  if (!brief) return <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center"><div className="text-slate-500">Brief not found</div></div>;

  const sc = STATUS_CONFIG[brief.status] || STATUS_CONFIG.hypothesis;
  const isQuickCreate = brief.source === "quick_create";
  const enrichStyle = ENRICHMENT_STYLES[brief.enrichment_readiness] || ENRICHMENT_STYLES.not_ready_for_analysis;
  const sigStyle = SIGNAL_STYLES[brief.signal_strength] || SIGNAL_STYLES.medium;

  return (
    <div className="min-h-screen bg-[#0a0e17] text-slate-200" data-testid="brief-detail-page">
      <div className="border-b border-slate-800/60 bg-[#0d1220]">
        <div className="max-w-[1200px] mx-auto px-6 py-4">
          <Button size="sm" variant="ghost" className="text-slate-500 hover:text-slate-300 -ml-2 mb-2"
            onClick={() => navigate("/commercial-briefs")} data-testid="back-to-briefs-btn">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Briefs
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="brief-detail-title">
                {brief.account_name}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={`text-[9px] ${sc.bg} ${sc.text} ${sc.border} border`}>{sc.label}</Badge>
                <span className="text-xs text-slate-500">Created {brief.created_at?.slice(0, 10)} by {brief.created_by}</span>
                {brief.source === "entry_intelligence" && <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500">From Analysis</Badge>}
                {brief.opportunity_source === "email_ingest" && <Badge variant="outline" className="text-[9px] border-blue-700/40 text-blue-400">Email Intel</Badge>}
                {brief.source === "quick_create" && (
                  <>
                    <Badge variant="outline" className="text-[9px] border-amber-700/40 text-amber-500">Quick Capture</Badge>
                    <Badge className={`text-[9px] ${enrichStyle.bg} ${enrichStyle.text} ${enrichStyle.border} border`}>{enrichStyle.label}</Badge>
                  </>
                )}
                {brief.source === "quick_create_enriched" && (
                  <>
                    <Badge variant="outline" className="text-[9px] border-violet-700/40 text-violet-400">Quick Capture → Enriched</Badge>
                    {brief.analysis_history?.length > 0 && (
                      <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500">
                        <History className="w-2.5 h-2.5 mr-1" />{brief.analysis_history.length} analysis{brief.analysis_history.length > 1 ? 'es' : ''}
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Quick Create: Show Analyze button if ready */}
              {brief.source === "quick_create" && brief.enrichment_readiness !== "not_ready_for_analysis" && (
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={() => navigate(`/entry-intelligence?enrich=${brief.brief_id}`)} data-testid="analyze-btn">
                  <Target className="w-3.5 h-3.5 mr-1.5" /> Analyze with Entry Intelligence
                </Button>
              )}
              {/* Enriched: Show Refresh Analysis button */}
              {brief.source === "quick_create_enriched" && (
                <Button size="sm" variant="outline" className="border-violet-700/50 text-violet-400 hover:bg-violet-950/30"
                  onClick={() => navigate(`/entry-intelligence?enrich=${brief.brief_id}`)} data-testid="refresh-analysis-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh Analysis
                </Button>
              )}
              <StatusTransitionControl brief={brief} onUpdate={fetchBrief} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-6 space-y-5">
        {/* Quick Create enrichment prompt (only for non-enriched briefs that need more context) */}
        {brief.source === "quick_create" && brief.enrichment_readiness === "not_ready_for_analysis" && (
          <div className="rounded-lg border bg-slate-900/50 border-slate-700/40 p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-200">This hypothesis needs enrichment before analysis</p>
                <p className="text-xs text-slate-500 mt-1">
                  Add sector, buyer contact, or tension hypothesis to enable Entry Intelligence analysis.
                </p>
              </div>
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 shrink-0">
                <Pencil className="w-3 h-3 mr-1" /> Edit Brief
              </Button>
            </div>
          </div>
        )}

        {/* Analysis Provenance (for enriched briefs) */}
        {brief.source === "quick_create_enriched" && brief.analysis_provenance && (
          <div className="rounded-lg border bg-violet-950/20 border-violet-800/40 p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-violet-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-violet-300">Analysis Provenance</p>
                <div className="grid grid-cols-3 gap-4 mt-2 text-xs">
                  <div>
                    <span className="text-slate-500">Analyzed:</span>
                    <span className="ml-2 text-slate-300">{brief.analysis_provenance.analyzed_at?.slice(0, 10)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">By:</span>
                    <span className="ml-2 text-slate-300">{brief.analysis_provenance.analyzed_by}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Engine:</span>
                    <span className="ml-2 text-slate-300">v{brief.analysis_provenance.engine_version}</span>
                  </div>
                </div>
                {brief.analysis_provenance.prefilled_fields?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    Pre-filled: {brief.analysis_provenance.prefilled_fields.join(", ")}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick Create Source Info (show initial capture for both quick_create and quick_create_enriched) */}
        {(brief.source === "quick_create" || brief.source === "quick_create_enriched") && (
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-[#111827] border-slate-800/60">
              <CardContent className="pt-4 pb-3">
                <SectionLabel>Opportunity Source</SectionLabel>
                <p className="text-sm font-medium text-slate-200 mt-1">
                  {OPPORTUNITY_SOURCES.find(s => s.value === brief.opportunity_source)?.label || brief.opportunity_source || "—"}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#111827] border-slate-800/60">
              <CardContent className="pt-4 pb-3">
                <SectionLabel>Signal Strength</SectionLabel>
                <div className="flex items-center gap-2 mt-1">
                  <Signal className={`w-4 h-4 ${sigStyle.text}`} />
                  <p className={`text-sm font-medium capitalize ${sigStyle.text}`}>{brief.signal_strength || "Medium"}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#111827] border-slate-800/60">
              <CardContent className="pt-4 pb-3">
                <SectionLabel>Known Buyer / Contact</SectionLabel>
                <p className="text-sm text-slate-300 mt-1">{brief.known_buyer_contact || "—"}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#111827] border-slate-800/60">
              <CardContent className="pt-4 pb-3">
                <SectionLabel>Next Conversation Intent</SectionLabel>
                <p className="text-sm text-slate-300 mt-1">{brief.next_conversation_intent || "—"}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Initial Hypothesis for Quick Create — show for both quick_create and quick_create_enriched (always preserve original) */}
        {(brief.source === "quick_create" || brief.source === "quick_create_enriched") && brief.initial_capture?.initial_tension_hypothesis && (
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Original Commercial Hypothesis (from capture)</SectionLabel>
              <p className="text-sm text-slate-300 mt-1 leading-relaxed italic">"{brief.initial_capture.initial_tension_hypothesis}"</p>
              {brief.initial_capture.captured_at && (
                <p className="text-[10px] text-slate-500 mt-2">Captured {brief.initial_capture.captured_at?.slice(0, 10)} by {brief.initial_capture.captured_by}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Hold state */}
        {brief.hold_active && (
          <div className={`rounded-lg border p-4 ${brief.hold_type === "poor_commercial_fit" ? "bg-red-950/30 border-red-800/40" : "bg-amber-950/20 border-amber-800/30"}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${brief.hold_type === "poor_commercial_fit" ? "text-red-400" : "text-amber-400"}`} />
              <div>
                <p className={`text-sm font-semibold ${brief.hold_type === "poor_commercial_fit" ? "text-red-300" : "text-amber-300"}`}>
                  {brief.hold_type === "poor_commercial_fit" ? "Poor Commercial Fit" : "Insufficient Information"}
                </p>
                <p className="text-xs text-slate-400 mt-1">{brief.hold_message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Email Intelligence (for email-ingested briefs) */}
        {brief.opportunity_source === "email_ingest" && (
          <div className="space-y-4">
            {/* Email Provenance Card */}
            {brief.email_provenance && (
              <Card className="bg-[#111827] border-slate-800/60">
                <CardContent className="pt-4 pb-3">
                  <SectionLabel>Email Source</SectionLabel>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">From</p>
                      <p className="text-sm text-slate-300 mt-0.5">{brief.email_provenance.from_name || brief.email_provenance.from || "—"}</p>
                      {brief.email_provenance.from && <p className="text-[10px] text-slate-500">{brief.email_provenance.from}</p>}
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Subject</p>
                      <p className="text-sm text-slate-300 mt-0.5">{brief.email_provenance.subject || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Received</p>
                      <p className="text-sm text-slate-300 mt-0.5">{brief.email_provenance.received_at || "—"}</p>
                      {brief.email_provenance.is_bcc && (
                        <Badge variant="outline" className="text-[9px] border-violet-700/40 text-violet-400 mt-1">BCC'd to Rex</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key Insights + Next Steps */}
            {(brief.key_insights?.length > 0 || brief.suggested_next_steps?.length > 0) && (
              <div className="grid grid-cols-2 gap-4">
                {brief.key_insights?.length > 0 && (
                  <Card className="bg-[#111827] border-slate-800/60">
                    <CardContent className="pt-4 pb-3">
                      <SectionLabel>Key Insights</SectionLabel>
                      <ul className="space-y-1.5 mt-2">{brief.key_insights.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
                          <Zap className="w-3 h-3 text-blue-500 mt-0.5 shrink-0" /><span className="leading-tight">{insight}</span>
                        </li>
                      ))}</ul>
                    </CardContent>
                  </Card>
                )}
                {brief.suggested_next_steps?.length > 0 && (
                  <Card className="bg-[#111827] border-slate-800/60">
                    <CardContent className="pt-4 pb-3">
                      <SectionLabel>Suggested Next Steps</SectionLabel>
                      <ul className="space-y-1.5 mt-2">{brief.suggested_next_steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
                          <ArrowRight className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" /><span className="leading-tight">{step}</span>
                        </li>
                      ))}</ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Deal Context */}
            {brief.deal_context?.relates_to_active_deal && (
              <Card className="bg-[#111827] border-emerald-800/40">
                <CardContent className="pt-4 pb-3">
                  <SectionLabel>Deal Intelligence</SectionLabel>
                  <div className="space-y-2 mt-2">
                    {brief.deal_context.suggested_stage && (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-900/40 text-emerald-400 border-emerald-800/40 text-[10px]">{brief.deal_context.suggested_stage}</Badge>
                        {brief.deal_context.stage_change_reason && (
                          <span className="text-xs text-slate-400">{brief.deal_context.stage_change_reason}</span>
                        )}
                      </div>
                    )}
                    {brief.deal_context.deal_update_summary && (
                      <p className="text-xs text-slate-300 leading-relaxed">{brief.deal_context.deal_update_summary}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Top row: Wedge + Ownership + Posture */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-5">
            <Card className="bg-[#111827] border-slate-800/60">
              <CardContent className="pt-4 pb-3">
                <SectionLabel>Selected Wedge</SectionLabel>
                <p className="text-base font-bold text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }}>{brief.selected_wedge || "—"}</p>
                {brief.wedge_reasoning && <p className="text-xs text-slate-400 mt-2 leading-relaxed">{brief.wedge_reasoning}</p>}
              </CardContent>
            </Card>
          </div>
          <div className="col-span-4">
            <Card className="bg-[#111827] border-slate-800/60">
              <CardContent className="pt-4 pb-3">
                <SectionLabel>Commercial Owner</SectionLabel>
                <div className="flex items-center gap-2 mt-1">
                  <User className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-200">{brief.commercial_owner || "Unassigned"}</p>
                    {brief.owner_role && <p className="text-[10px] text-slate-500">{brief.owner_role}</p>}
                  </div>
                </div>
                <OwnerEditor brief={brief} onUpdate={fetchBrief} />
              </CardContent>
            </Card>
          </div>
          <div className="col-span-3">
            <Card className="bg-[#111827] border-slate-800/60">
              <CardContent className="pt-4 pb-3">
                <SectionLabel>Posture</SectionLabel>
                <p className={`text-sm font-semibold mt-1 ${POSTURE_STYLES[typeof brief.commercial_posture === 'object' ? brief.commercial_posture.posture : brief.commercial_posture] || "text-slate-400"}`}>
                  {typeof brief.commercial_posture === 'object' ? brief.commercial_posture.posture : brief.commercial_posture || "—"}
                </p>
                {(brief.posture_reasoning || (typeof brief.commercial_posture === 'object' && brief.commercial_posture.reasoning)) && 
                  <p className="text-[10px] text-slate-500 mt-1">{brief.posture_reasoning || brief.commercial_posture?.reasoning}</p>
                }
                <div className="mt-3">
                  <SectionLabel>Confidence</SectionLabel>
                  <span className={`text-sm font-bold ${CONFIDENCE_STYLES[brief.confidence_level] || "text-slate-400"}`}>
                    {brief.confidence_level || "—"}
                  </span>
                  {brief.confidence_score > 0 && <span className="text-xs text-slate-600 ml-1 font-mono">{Math.round(brief.confidence_score * 100)}%</span>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Confidence Drivers + Uncertainty */}
        {(brief.confidence_drivers?.length > 0 || brief.uncertainty_flags?.length > 0) && (
          <div className="grid grid-cols-2 gap-4">
            {brief.confidence_drivers?.length > 0 && (
              <Card className="bg-[#111827] border-slate-800/60">
                <CardContent className="pt-4 pb-3">
                  <SectionLabel>Confidence Drivers</SectionLabel>
                  <ul className="space-y-1.5">{brief.confidence_drivers.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
                      <Zap className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" /><span className="leading-tight">{d}</span>
                    </li>
                  ))}</ul>
                </CardContent>
              </Card>
            )}
            {brief.uncertainty_flags?.length > 0 && (
              <Card className="bg-[#111827] border-slate-800/60">
                <CardContent className="pt-4 pb-3">
                  <SectionLabel>Uncertainty Flags</SectionLabel>
                  <ul className="space-y-1.5">{brief.uncertainty_flags.map((u, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                      <EyeOff className="w-3 h-3 text-amber-500/70 mt-0.5 shrink-0" /><span className="leading-tight">{u}</span>
                    </li>
                  ))}</ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* What Strengthens + Risk */}
        {brief.what_strengthens?.length > 0 && (
          <Card className="bg-sky-950/20 border-sky-800/30">
            <CardContent className="pt-4 pb-3">
              <SectionLabel>What Would Strengthen This Recommendation</SectionLabel>
              <ul className="space-y-1.5">{brief.what_strengthens.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-sky-300/80">
                  <ArrowRight className="w-3 h-3 text-sky-500 mt-0.5 shrink-0" /><span className="leading-tight">{s}</span>
                </li>
              ))}</ul>
            </CardContent>
          </Card>
        )}

        {/* Proof */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel><span className="flex items-center gap-1"><Unlock className="w-3 h-3 text-emerald-500" /> Proof Available</span></SectionLabel>
              {brief.proof_available?.length > 0 ? (
                <ul className="space-y-1.5">{brief.proof_available.map((p, i) => (
                  <li key={i} className="text-xs text-slate-300 leading-tight pl-3 border-l-2 border-emerald-700/40">{p}</li>
                ))}</ul>
              ) : <p className="text-xs text-slate-600">None documented</p>}
            </CardContent>
          </Card>
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel><span className="flex items-center gap-1"><Lock className="w-3 h-3 text-amber-500" /> Proof Still Needed</span></SectionLabel>
              {brief.proof_needed?.length > 0 ? (
                <ul className="space-y-1.5">{brief.proof_needed.map((p, i) => (
                  <li key={i} className="text-xs text-slate-400 leading-tight pl-3 border-l-2 border-amber-700/30">{p}</li>
                ))}</ul>
              ) : <p className="text-xs text-slate-600">None documented</p>}
            </CardContent>
          </Card>
        </div>

        {/* Expansion + Buyers + Risk */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Expansion Hypothesis</SectionLabel>
              <div className="space-y-1">{(brief.expansion_hypothesis || []).map((e, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-emerald-800/40 text-emerald-400/80 bg-emerald-900/10 mr-1">{e}</Badge>
              ))}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Economic Buyers</SectionLabel>
              <div className="space-y-1">{(brief.economic_buyers || []).map((b, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-slate-700 text-slate-300 mr-1">{b}</Badge>
              ))}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Outcome Quality</SectionLabel>
              <OutcomeEditor brief={brief} onUpdate={fetchBrief} />
            </CardContent>
          </Card>
        </div>

        {/* Risk */}
        {brief.risk_if_wrong_wedge && (
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Risk If Wrong Wedge Chosen</SectionLabel>
              <p className="text-xs text-slate-400 leading-relaxed">{brief.risk_if_wrong_wedge}</p>
            </CardContent>
          </Card>
        )}

        {/* Status History */}
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel>Status History</SectionLabel>
            <div className="space-y-2 mt-1">
              {(brief.status_history || []).map((h, i) => {
                const hsc = STATUS_CONFIG[h.status] || {};
                return (
                  <div key={i} className="flex items-start gap-3 text-xs">
                    <div className="w-2 h-2 rounded-full bg-slate-600 mt-1.5 shrink-0" />
                    <div>
                      <span className={`font-medium ${hsc.text || "text-slate-300"}`}>{hsc.label || h.status}</span>
                      <span className="text-slate-600 ml-2">{h.changed_at?.slice(0, 16).replace("T", " ")}</span>
                      <span className="text-slate-500 ml-2">by {h.changed_by}</span>
                      {h.note && <p className="text-slate-500 mt-0.5 italic">{h.note}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Annotations */}
        <AnnotationsSection brief={brief} onUpdate={fetchBrief} />

        {/* Content Extractions */}
        <ContentExtractionsSection brief={brief} onUpdate={fetchBrief} />

        {/* Comparative Reasoning */}
        {brief.comparative_reasoning?.length > 0 && (
          <Card className="bg-[#111827] border-slate-800/60">
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Comparative Reasoning</SectionLabel>
              <div className="space-y-2 mt-1">
                {brief.comparative_reasoning.map((c, i) => (
                  <div key={i} className="p-3 rounded-md bg-slate-900/40 border border-slate-800/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-300">{c.entry_point}</span>
                      <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500">{c.verdict}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1"><span className="text-slate-400">Why not:</span> {c.why_not_first}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════

function StatusTransitionControl({ brief, onUpdate }) {
  const [transitioning, setTransitioning] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [note, setNote] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const doTransition = async () => {
    if (!selectedStatus) return;
    setTransitioning(true);
    try {
      const res = await fetch(`${API}/commercial-briefs/${brief.brief_id}/transition`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_status: selectedStatus, note }),
      });
      if (res.ok) { toast.success(`Status: ${STATUS_CONFIG[selectedStatus]?.label}`); onUpdate(); setShowAll(false); setNote(""); setSelectedStatus(""); }
      else toast.error("Transition failed");
    } catch { toast.error("Network error"); }
    finally { setTransitioning(false); }
  };

  if (!showAll) {
    return (
      <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 hover:text-slate-200"
        onClick={() => setShowAll(true)} data-testid="transition-btn">
        <Send className="w-3.5 h-3.5 mr-1.5" /> Advance Status
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="transition-panel">
      <select className="bg-slate-900 border border-slate-700/60 rounded-md px-2 py-1.5 text-xs text-slate-300"
        value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-testid="transition-select">
        <option value="">Select status...</option>
        {VALID_STATUSES.filter(s => s !== brief.status).map(s => (
          <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>
        ))}
      </select>
      <input className="bg-slate-900 border border-slate-700/60 rounded-md px-2 py-1.5 text-xs text-slate-300 w-48"
        placeholder="Optional note..." value={note} onChange={e => setNote(e.target.value)} data-testid="transition-note" />
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
        onClick={doTransition} disabled={!selectedStatus || transitioning} data-testid="transition-confirm-btn">
        {transitioning ? "..." : "Confirm"}
      </Button>
      <Button size="sm" variant="ghost" className="text-slate-500 text-xs" onClick={() => setShowAll(false)}>Cancel</Button>
    </div>
  );
}

function OwnerEditor({ brief, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [owner, setOwner] = useState(brief.commercial_owner || "");
  const [role, setRole] = useState(brief.owner_role || "");

  const save = async () => {
    try {
      const res = await fetch(`${API}/commercial-briefs/${brief.brief_id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commercial_owner: owner, owner_role: role }),
      });
      if (res.ok) { toast.success("Owner updated"); onUpdate(); setEditing(false); }
    } catch {}
  };

  if (!editing) {
    return (
      <button className="text-[10px] text-slate-600 hover:text-slate-400 mt-1.5 flex items-center gap-1" onClick={() => setEditing(true)}>
        <Pencil className="w-2.5 h-2.5" /> Change owner
      </button>
    );
  }
  return (
    <div className="mt-2 space-y-1.5">
      <input className="w-full bg-slate-900 border border-slate-700/60 rounded px-2 py-1 text-xs text-slate-300"
        placeholder="Owner name" value={owner} onChange={e => setOwner(e.target.value)} />
      <input className="w-full bg-slate-900 border border-slate-700/60 rounded px-2 py-1 text-xs text-slate-300"
        placeholder="Role" value={role} onChange={e => setRole(e.target.value)} />
      <div className="flex gap-1">
        <Button size="sm" className="text-xs h-6 bg-emerald-600" onClick={save}>Save</Button>
        <Button size="sm" variant="ghost" className="text-xs h-6 text-slate-500" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function OutcomeEditor({ brief, onUpdate }) {
  const save = async (val) => {
    try {
      const res = await fetch(`${API}/commercial-briefs/${brief.brief_id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ outcome_quality: val }),
      });
      if (res.ok) { toast.success("Outcome updated"); onUpdate(); }
    } catch {}
  };

  return (
    <div className="space-y-1 mt-1">
      {brief.outcome_quality ? (
        <div className="flex items-center gap-2">
          <Badge className="text-[10px] bg-slate-800 text-slate-300 border border-slate-700">{brief.outcome_quality}</Badge>
          <button className="text-[10px] text-slate-600 hover:text-slate-400" onClick={() => save("")}>Clear</button>
        </div>
      ) : (
        <select className="bg-slate-900 border border-slate-700/60 rounded px-2 py-1 text-xs text-slate-300 w-full"
          value="" onChange={e => e.target.value && save(e.target.value)} data-testid="outcome-select">
          <option value="">Set outcome quality...</option>
          {OUTCOME_QUALITIES.map(q => <option key={q} value={q}>{q}</option>)}
        </select>
      )}
    </div>
  );
}

function AnnotationsSection({ brief, onUpdate }) {
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);

  const addAnnotation = async () => {
    if (!text.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/commercial-briefs/${brief.brief_id}/annotations`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: text.trim() }),
      });
      if (res.ok) { setText(""); onUpdate(); }
    } catch {} finally { setAdding(false); }
  };

  return (
    <Card className="bg-[#111827] border-slate-800/60">
      <CardContent className="pt-4 pb-3">
        <SectionLabel>Annotations</SectionLabel>
        {brief.annotations?.length > 0 && (
          <div className="space-y-2 mb-3">
            {brief.annotations.map((a) => (
              <div key={a.annotation_id} className="p-2.5 rounded-md bg-slate-900/40 border border-slate-800/30">
                <p className="text-xs text-slate-300 leading-relaxed">{a.content}</p>
                <p className="text-[10px] text-slate-600 mt-1">{a.author} — {a.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input className="flex-1 bg-slate-900 border border-slate-700/60 rounded-md px-3 py-1.5 text-xs text-slate-300"
            placeholder="Add an annotation..." value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addAnnotation()} data-testid="annotation-input" />
          <Button size="sm" className="bg-slate-700 hover:bg-slate-600 text-xs" onClick={addAnnotation} disabled={adding || !text.trim()}>
            <MessageSquare className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ContentExtractionsSection({ brief, onUpdate }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ theme: "", audience: "", strongest_headline_angle: "", proof_gap_revealed: "", urgency_level: "medium" });
  const [adding, setAdding] = useState(false);

  const addExtraction = async () => {
    if (!form.theme.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/commercial-briefs/${brief.brief_id}/content-extraction`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) { setForm({ theme: "", audience: "", strongest_headline_angle: "", proof_gap_revealed: "", urgency_level: "medium" }); setShow(false); onUpdate(); toast.success("Content extraction added"); }
    } catch {} finally { setAdding(false); }
  };

  return (
    <Card className="bg-[#111827] border-slate-800/60">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Content Extractions</SectionLabel>
          <Button size="sm" variant="ghost" className="text-xs text-slate-500 hover:text-emerald-400 h-6"
            onClick={() => setShow(!show)} data-testid="add-content-extraction-btn">
            <Plus className="w-3 h-3 mr-1" /> Extract Content Theme
          </Button>
        </div>
        {brief.content_extractions?.length > 0 && (
          <div className="space-y-2 mb-3">
            {brief.content_extractions.map((ex) => (
              <div key={ex.extraction_id} className="p-3 rounded-md bg-teal-950/20 border border-teal-800/30">
                <p className="text-sm font-medium text-teal-300">{ex.theme}</p>
                {ex.audience && <p className="text-[10px] text-slate-400 mt-0.5">Audience: {ex.audience}</p>}
                {ex.strongest_headline_angle && <p className="text-xs text-slate-300 mt-1 italic">"{ex.strongest_headline_angle}"</p>}
                {ex.proof_gap_revealed && <p className="text-[10px] text-amber-400/70 mt-1">Proof gap: {ex.proof_gap_revealed}</p>}
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className={`text-[9px] ${
                    ex.urgency_level === "high" ? "border-red-700/40 text-red-400" :
                    ex.urgency_level === "medium" ? "border-amber-700/40 text-amber-400" :
                    "border-slate-700 text-slate-400"
                  }`}>{ex.urgency_level} urgency</Badge>
                  <span className="text-[10px] text-slate-600">by {ex.extracted_by} — {ex.extracted_at?.slice(0, 10)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {show && (
          <div className="space-y-2 p-3 rounded-md bg-slate-900/40 border border-slate-800/40">
            <input className="w-full bg-slate-900 border border-slate-700/60 rounded px-2 py-1.5 text-xs text-slate-300"
              placeholder="Theme *" value={form.theme} onChange={e => setForm({...form, theme: e.target.value})} />
            <input className="w-full bg-slate-900 border border-slate-700/60 rounded px-2 py-1.5 text-xs text-slate-300"
              placeholder="Target audience" value={form.audience} onChange={e => setForm({...form, audience: e.target.value})} />
            <input className="w-full bg-slate-900 border border-slate-700/60 rounded px-2 py-1.5 text-xs text-slate-300"
              placeholder="Strongest headline angle" value={form.strongest_headline_angle} onChange={e => setForm({...form, strongest_headline_angle: e.target.value})} />
            <input className="w-full bg-slate-900 border border-slate-700/60 rounded px-2 py-1.5 text-xs text-slate-300"
              placeholder="Proof gap revealed" value={form.proof_gap_revealed} onChange={e => setForm({...form, proof_gap_revealed: e.target.value})} />
            <select className="bg-slate-900 border border-slate-700/60 rounded px-2 py-1.5 text-xs text-slate-300"
              value={form.urgency_level} onChange={e => setForm({...form, urgency_level: e.target.value})}>
              <option value="low">Low urgency</option>
              <option value="medium">Medium urgency</option>
              <option value="high">High urgency</option>
            </select>
            <div className="flex gap-1.5">
              <Button size="sm" className="text-xs bg-teal-600 hover:bg-teal-700" onClick={addExtraction} disabled={adding || !form.theme.trim()}>
                {adding ? "..." : "Save Extraction"}
              </Button>
              <Button size="sm" variant="ghost" className="text-xs text-slate-500" onClick={() => setShow(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
